/**
 * RoiDashboardPage — Super-Admin-only Credit Analytics & ROI tracker.
 *
 *   • Summary Table of every client project (Job ID → total ECU, monthly
 *     credits, monthly cost, monthly fee, net profit, 24h burn, threshold,
 *     alert badge).
 *   • Add-Client form + inline monthly-fee / threshold editing.
 *   • Manual burn quick-add and CSV import per project.
 *   • Global credit rate + markup panel — pre-filled from server.py so the
 *     numbers reconcile with /admin/billing out of the box.
 *
 * All server routes are gated by SUPER_ADMIN_EMAILS in roi.py — this page
 * still renders for any partner but every API call short-circuits with 403
 * for non-super admins. The route itself is hidden from the System Console
 * menu unless the logged-in user's email is in the allowlist.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, AlertTriangle, TrendingUp, TrendingDown,
  Upload, ChevronDown, Save, Settings2, DollarSign,
} from "lucide-react";
import { apiClient } from "@/lib/auth";
import BurnChart from "@/components/roi/BurnChart";

const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n || 0);
const int = (n) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n || 0);
const dec4 = (n) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(n || 0);

const inputCls = "bg-black/40 border border-white/10 focus:border-nex-gold/60 outline-none px-3 py-2 text-nex-silver font-mono text-sm transition-all duration-200 w-full";

export default function RoiDashboardPage() {
  const [loading, setLoading]   = useState(true);
  const [rows, setRows]         = useState([]);
  const [totals, setTotals]     = useState({});
  const [config, setConfig]     = useState({});
  const [alertCnt, setAlertCnt] = useState(0);
  const [showAdd, setShowAdd]   = useState(false);
  const [showCfg, setShowCfg]   = useState(false);
  const [expanded, setExpanded] = useState(null);
  // Bumped every time we mutate burn/project data so BurnChart refetches.
  const [reloadKey, setReloadKey] = useState(0);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/roi/summary");
      setRows(data.projects || []);
      setTotals(data.totals || {});
      setConfig(data.config || {});
      setAlertCnt(data.alerts_count || 0);
      setReloadKey((k) => k + 1);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) toast.error("Super Admin only — access denied");
      else toast.error(e?.response?.data?.detail || "Load failed");
      setRows([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-nex-silver">
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/os"
              data-testid="roi-back-to-os"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/10 hover:border-nex-gold/50 text-nex-chrome hover:text-nex-gold font-mono text-[0.65rem] tracking-[0.22em] uppercase transition-all duration-200 no-underline"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to OS
            </Link>
            <div>
              <div className="font-mono text-[0.6rem] tracking-[0.24em] text-nex-gold uppercase">
                // Credit Analytics · Super Admin
              </div>
              <div className="text-nex-silver font-serif text-xl font-bold">ROI Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowCfg((v) => !v)}
              data-testid="roi-config-toggle"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/10 hover:border-nex-gold/50 text-nex-chrome hover:text-nex-gold font-mono text-[0.65rem] tracking-[0.22em] uppercase transition-all"
            >
              <Settings2 className="w-3.5 h-3.5" /> Rate Config
            </button>
            <button
              type="button"
              onClick={() => setShowAdd((v) => !v)}
              data-testid="roi-add-project-toggle"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-nex-gold text-nex-navy font-mono text-[0.65rem] tracking-[0.22em] uppercase hover:bg-nex-goldbright transition-all"
            >
              <Plus className="w-3.5 h-3.5" /> Add Client
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
        <SummaryStrip totals={totals} config={config} alertCnt={alertCnt} projectCount={rows.length} />
        {showCfg && <ConfigPanel config={config} onSaved={reload} />}
        {showAdd && <AddClientForm onCreated={() => { setShowAdd(false); reload(); }} />}

        <BurnChart reloadKey={reloadKey} />

        <div className="border border-white/10 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-white/10 bg-white/[0.03] flex items-center justify-between">
            <div className="font-mono text-[0.6rem] tracking-[0.24em] text-nex-gold uppercase">
              // Client Projects · {rows.length}
            </div>
            {loading && <span className="font-mono text-[0.6rem] text-nex-muted">Loading…</span>}
          </div>
          <ProjectsTable
            rows={rows}
            config={config}
            expanded={expanded}
            setExpanded={setExpanded}
            onChanged={reload}
          />
        </div>
      </main>
    </div>
  );
}

/* ============ Summary Strip ============ */
function SummaryStrip({ totals, config, alertCnt, projectCount }) {
  const cards = [
    { label: "PROJECTS",       value: int(projectCount),         icon: DollarSign, testId: "roi-strip-projects" },
    { label: "30D CREDITS",    value: int(totals.monthly_credits), icon: TrendingUp, testId: "roi-strip-credits" },
    { label: "30D COST",       value: money(totals.monthly_cost), icon: TrendingDown, testId: "roi-strip-cost" },
    { label: "30D REVENUE",    value: money(totals.monthly_fee),  icon: DollarSign, testId: "roi-strip-fee" },
    { label: "30D NET PROFIT", value: money(totals.net_profit),   icon: TrendingUp, testId: "roi-strip-profit", tone: (totals.net_profit || 0) >= 0 ? "good" : "bad" },
    { label: "ALERTS",         value: int(alertCnt),              icon: AlertTriangle, testId: "roi-strip-alerts", tone: alertCnt > 0 ? "bad" : "good" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      {cards.map(({ label, value, icon: Icon, testId, tone }) => (
        <div
          key={label}
          data-testid={testId}
          className={`border p-3 bg-white/[0.02] ${
            tone === "good" ? "border-emerald-500/30" : tone === "bad" ? "border-red-500/40" : "border-white/10"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="font-mono text-[0.55rem] tracking-[0.22em] text-nex-muted uppercase">{label}</span>
            <Icon className="w-3.5 h-3.5 text-nex-chrome" />
          </div>
          <div className={`mt-1 text-xl font-serif font-bold ${
            tone === "bad" ? "text-red-400" : tone === "good" ? "text-emerald-400" : "text-nex-silver"
          }`}>
            {value}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============ Config Panel ============ */
function ConfigPanel({ config, onSaved }) {
  const [rate, setRate]   = useState(config.credit_price_per_raw ?? "");
  const [markup, setMarkup] = useState(config.markup ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRate(config.credit_price_per_raw ?? "");
    setMarkup(config.markup ?? "");
  }, [config.credit_price_per_raw, config.markup]);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put("/admin/roi/config", {
        credit_price_per_raw: rate === "" ? null : Number(rate),
        markup:               markup === "" ? null : Number(markup),
      });
      toast.success("Rate config saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-nex-gold/30 bg-white/[0.02] p-4" data-testid="roi-config-panel">
      <div className="font-mono text-[0.6rem] tracking-[0.24em] text-nex-gold uppercase mb-3">// Global Rate Config</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <label className="flex flex-col gap-1">
          <span className="font-mono text-[0.6rem] tracking-[0.22em] text-nex-muted uppercase">$ / Raw Credit</span>
          <input type="number" step="0.0001" className={inputCls} value={rate} onChange={(e) => setRate(e.target.value)} data-testid="roi-config-rate" />
          <span className="text-[0.65rem] text-nex-muted">Default $0.20833 (markup × unit cost)</span>
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-mono text-[0.6rem] tracking-[0.22em] text-nex-muted uppercase">Markup ×</span>
          <input type="number" step="0.01" className={inputCls} value={markup} onChange={(e) => setMarkup(e.target.value)} data-testid="roi-config-markup" />
          <span className="text-[0.65rem] text-nex-muted">Current billing markup is 1.25</span>
        </label>
        <button type="button" onClick={save} disabled={saving} data-testid="roi-config-save" className="px-4 py-2 bg-nex-gold text-nex-navy font-mono text-[0.65rem] tracking-[0.22em] uppercase hover:bg-nex-goldbright disabled:opacity-60 transition-all">
          <Save className="w-3.5 h-3.5 inline mr-1" /> {saving ? "Saving…" : "Save Rate"}
        </button>
      </div>
      <div className="mt-2 font-mono text-[0.65rem] text-nex-muted">
        Effective unit cost: <span className="text-nex-silver">${dec4(config.unit_cost)}</span>
        {" · "}Updated: <span className="text-nex-silver">{config.updated_at?.split("T")[0]}</span>
      </div>
    </div>
  );
}

/* ============ Add Client Form ============ */
function AddClientForm({ onCreated }) {
  const [f, setF]       = useState({ job_id: "", name: "", monthly_fee: "", threshold_24h: "", credit_price_override: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (ev) => {
    ev?.preventDefault();
    if (!f.job_id || !f.name) { toast.error("Job ID and Name are required"); return; }
    setSaving(true);
    try {
      await apiClient.post("/admin/roi/projects", {
        job_id:                f.job_id.trim(),
        name:                  f.name.trim(),
        monthly_fee:           Number(f.monthly_fee || 0),
        threshold_24h:         Number(f.threshold_24h || 0),
        credit_price_override: f.credit_price_override === "" ? null : Number(f.credit_price_override),
      });
      toast.success(`Added ${f.name}`);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="border border-nex-gold/30 bg-white/[0.02] p-4 grid grid-cols-1 md:grid-cols-6 gap-3" data-testid="roi-add-client-form">
      <label className="flex flex-col gap-1 md:col-span-2">
        <span className="font-mono text-[0.6rem] tracking-[0.22em] text-nex-muted uppercase">Job ID *</span>
        <input className={inputCls} value={f.job_id} onChange={set("job_id")} placeholder="acme-crm-2026" autoFocus data-testid="roi-add-job-id" />
      </label>
      <label className="flex flex-col gap-1 md:col-span-2">
        <span className="font-mono text-[0.6rem] tracking-[0.22em] text-nex-muted uppercase">Client Name *</span>
        <input className={inputCls} value={f.name} onChange={set("name")} placeholder="Acme CRM" data-testid="roi-add-name" />
      </label>
      <label className="flex flex-col gap-1">
        <span className="font-mono text-[0.6rem] tracking-[0.22em] text-nex-muted uppercase">Monthly Fee $</span>
        <input type="number" step="0.01" className={inputCls} value={f.monthly_fee} onChange={set("monthly_fee")} placeholder="5000" data-testid="roi-add-fee" />
      </label>
      <label className="flex flex-col gap-1">
        <span className="font-mono text-[0.6rem] tracking-[0.22em] text-nex-muted uppercase">24h Threshold</span>
        <input type="number" step="0.01" className={inputCls} value={f.threshold_24h} onChange={set("threshold_24h")} placeholder="300" data-testid="roi-add-threshold" />
      </label>
      <label className="flex flex-col gap-1 md:col-span-5">
        <span className="font-mono text-[0.6rem] tracking-[0.22em] text-nex-muted uppercase">Credit Price Override ($/raw credit — optional)</span>
        <input type="number" step="0.0001" className={inputCls} value={f.credit_price_override} onChange={set("credit_price_override")} placeholder="Leave blank to use global rate" data-testid="roi-add-price-override" />
      </label>
      <button type="submit" disabled={saving} data-testid="roi-add-submit" className="px-4 py-2 bg-nex-gold text-nex-navy font-mono text-[0.65rem] tracking-[0.22em] uppercase hover:bg-nex-goldbright disabled:opacity-60 transition-all self-end">
        {saving ? "Saving…" : "Add Client"}
      </button>
    </form>
  );
}

/* ============ Projects Table ============ */
function ProjectsTable({ rows, config, expanded, setExpanded, onChanged }) {
  if (rows.length === 0) {
    return <div className="p-8 text-center font-mono text-sm text-nex-muted" data-testid="roi-empty">No client projects yet. Click <b>Add Client</b> to start tracking ECU.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="roi-projects-table">
        <thead className="bg-white/[0.03] border-b border-white/10">
          <tr>
            {["", "Job ID", "Client", "Total ECU", "30D Credits", "30D Cost", "Monthly Fee", "Net Profit", "24h Burn", "Threshold", ""].map((h, i) => (
              <th key={i} className="px-3 py-2 text-left font-mono text-[0.55rem] tracking-[0.2em] text-nex-muted uppercase whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <ProjectRow
              key={p.id}
              p={p}
              config={config}
              isExpanded={expanded === p.id}
              toggle={() => setExpanded(expanded === p.id ? null : p.id)}
              onChanged={onChanged}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProjectRow({ p, config, isExpanded, toggle, onChanged }) {
  const netColor = p.net_profit >= 0 ? "text-emerald-400" : "text-red-400";
  return (
    <>
      <tr
        onClick={toggle}
        className={`border-b border-white/5 hover:bg-white/[0.04] cursor-pointer transition-colors ${p.alert ? "bg-red-500/[0.06]" : ""}`}
        data-testid={`roi-row-${p.job_id}`}
      >
        <td className="px-3 py-2">
          <ChevronDown className={`w-3.5 h-3.5 text-nex-muted transition-transform ${isExpanded ? "rotate-180" : ""}`} />
        </td>
        <td className="px-3 py-2 font-mono text-xs text-nex-silver whitespace-nowrap">{p.job_id}</td>
        <td className="px-3 py-2 font-medium text-nex-silver whitespace-nowrap">{p.name}</td>
        <td className="px-3 py-2 text-nex-silver font-mono">{int(p.total_ecu)}</td>
        <td className="px-3 py-2 text-nex-silver font-mono">{int(p.monthly_credits)}</td>
        <td className="px-3 py-2 text-red-300 font-mono">{money(p.monthly_cost)}</td>
        <td className="px-3 py-2 text-emerald-300 font-mono">{money(p.monthly_fee)}</td>
        <td className={`px-3 py-2 font-mono font-bold ${netColor}`}>{money(p.net_profit)}</td>
        <td className="px-3 py-2 text-nex-silver font-mono">{int(p.burn_24h)}</td>
        <td className="px-3 py-2 text-nex-muted font-mono">{p.threshold_24h ? int(p.threshold_24h) : "—"}</td>
        <td className="px-3 py-2">
          {p.alert && (
            <span
              data-testid={`roi-alert-${p.job_id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 border border-red-500/50 bg-red-500/15 text-red-300 font-mono text-[0.55rem] tracking-[0.16em] uppercase"
            >
              <AlertTriangle className="w-3 h-3" /> Over Threshold
            </span>
          )}
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={11} className="px-3 py-3 bg-black/40 border-b border-white/10">
            <ExpandedRow p={p} config={config} onChanged={onChanged} />
          </td>
        </tr>
      )}
    </>
  );
}

/* ============ Expanded Row (Edit / Burn / CSV / Archive) ============ */
function ExpandedRow({ p, config, onChanged }) {
  const [monthlyFee, setMonthlyFee]     = useState(p.monthly_fee);
  const [threshold, setThreshold]       = useState(p.threshold_24h);
  const [priceOverride, setPriceOverride] = useState(p.credit_price_override ?? "");
  const [savingEdit, setSavingEdit]     = useState(false);

  const [burnDate, setBurnDate]         = useState(new Date().toISOString().slice(0, 10));
  const [burnCredits, setBurnCredits]   = useState("");
  const [burnNotes, setBurnNotes]       = useState("");
  const [savingBurn, setSavingBurn]     = useState(false);

  const [csv, setCsv] = useState(null);
  const [importing, setImporting] = useState(false);

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await apiClient.put(`/admin/roi/projects/${p.id}`, {
        monthly_fee:           Number(monthlyFee),
        threshold_24h:         Number(threshold),
        credit_price_override: priceOverride === "" ? null : Number(priceOverride),
      });
      toast.success("Client updated");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingEdit(false); }
  };

  const logBurn = async (ev) => {
    ev?.preventDefault();
    if (!burnCredits || Number(burnCredits) <= 0) { toast.error("Enter credits > 0"); return; }
    setSavingBurn(true);
    try {
      await apiClient.post("/admin/roi/burn", {
        project_id:  p.id,
        date:        burnDate,
        raw_credits: Number(burnCredits),
        notes:       burnNotes,
      });
      toast.success(`Logged ${burnCredits} credits`);
      setBurnCredits(""); setBurnNotes("");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Log failed"); }
    finally { setSavingBurn(false); }
  };

  const uploadCsv = async () => {
    if (!csv) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", csv);
      const { data } = await apiClient.post(`/admin/roi/burn/csv?project_id=${p.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.inserted} rows${data.skipped?.length ? ` · ${data.skipped.length} skipped` : ""}`);
      setCsv(null);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const archive = async () => {
    if (!window.confirm(`Archive "${p.name}"? Burn entries remain in the DB.`)) return;
    try {
      await apiClient.delete(`/admin/roi/projects/${p.id}`);
      toast.success("Archived");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Archive failed"); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid={`roi-expanded-${p.job_id}`}>
      {/* Edit */}
      <div className="border border-white/10 p-3 space-y-2">
        <div className="font-mono text-[0.55rem] tracking-[0.22em] text-nex-gold uppercase">// Edit Client</div>
        <label className="flex flex-col gap-0.5">
          <span className="font-mono text-[0.55rem] text-nex-muted uppercase">Monthly Fee $</span>
          <input type="number" step="0.01" className={inputCls} value={monthlyFee} onChange={(e) => setMonthlyFee(e.target.value)} data-testid={`roi-edit-fee-${p.job_id}`} />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="font-mono text-[0.55rem] text-nex-muted uppercase">24h Threshold (raw credits)</span>
          <input type="number" step="0.01" className={inputCls} value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid={`roi-edit-threshold-${p.job_id}`} />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="font-mono text-[0.55rem] text-nex-muted uppercase">$ / Raw Credit (override)</span>
          <input type="number" step="0.0001" className={inputCls} value={priceOverride} onChange={(e) => setPriceOverride(e.target.value)} placeholder="Blank = global rate" />
        </label>
        <div className="flex items-center justify-between gap-2 pt-1">
          <button type="button" onClick={saveEdit} disabled={savingEdit} data-testid={`roi-save-edit-${p.job_id}`} className="flex-1 px-3 py-1.5 bg-nex-gold text-nex-navy font-mono text-[0.6rem] tracking-[0.22em] uppercase hover:bg-nex-goldbright disabled:opacity-60">
            <Save className="w-3 h-3 inline mr-1" /> {savingEdit ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={archive} className="px-2.5 py-1.5 border border-red-500/40 text-red-400 hover:bg-red-500/10 font-mono text-[0.6rem] tracking-[0.22em] uppercase">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Log Burn */}
      <form onSubmit={logBurn} className="border border-white/10 p-3 space-y-2">
        <div className="font-mono text-[0.55rem] tracking-[0.22em] text-nex-gold uppercase">// Log Burn</div>
        <label className="flex flex-col gap-0.5">
          <span className="font-mono text-[0.55rem] text-nex-muted uppercase">Date</span>
          <input type="date" className={inputCls} value={burnDate} onChange={(e) => setBurnDate(e.target.value)} data-testid={`roi-burn-date-${p.job_id}`} />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="font-mono text-[0.55rem] text-nex-muted uppercase">Raw Credits</span>
          <input type="number" step="0.01" className={inputCls} value={burnCredits} onChange={(e) => setBurnCredits(e.target.value)} placeholder="e.g. 42" data-testid={`roi-burn-credits-${p.job_id}`} />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="font-mono text-[0.55rem] text-nex-muted uppercase">Notes</span>
          <input className={inputCls} value={burnNotes} onChange={(e) => setBurnNotes(e.target.value)} placeholder="optional" />
        </label>
        <button type="submit" disabled={savingBurn} data-testid={`roi-burn-submit-${p.job_id}`} className="w-full px-3 py-1.5 bg-white/10 text-nex-silver font-mono text-[0.6rem] tracking-[0.22em] uppercase hover:bg-white/20 disabled:opacity-60">
          <Plus className="w-3 h-3 inline mr-1" /> {savingBurn ? "Logging…" : "Log Credits"}
        </button>
      </form>

      {/* CSV Import */}
      <div className="border border-white/10 p-3 space-y-2">
        <div className="font-mono text-[0.55rem] tracking-[0.22em] text-nex-gold uppercase">// CSV Backfill</div>
        <p className="text-[0.65rem] text-nex-muted font-mono">
          Headers required: <code className="text-nex-silver">date,raw_credits[,notes]</code>. Dates in YYYY-MM-DD.
        </p>
        <label className="flex flex-col gap-0.5">
          <input type="file" accept=".csv" onChange={(e) => setCsv(e.target.files?.[0] || null)} className="text-nex-silver text-xs font-mono file:mr-2 file:px-2 file:py-1 file:border-0 file:bg-white/10 file:text-nex-silver file:font-mono file:text-[0.6rem] file:uppercase file:tracking-widest" data-testid={`roi-csv-file-${p.job_id}`} />
        </label>
        <button type="button" onClick={uploadCsv} disabled={!csv || importing} data-testid={`roi-csv-upload-${p.job_id}`} className="w-full px-3 py-1.5 bg-white/10 text-nex-silver font-mono text-[0.6rem] tracking-[0.22em] uppercase hover:bg-white/20 disabled:opacity-40">
          <Upload className="w-3 h-3 inline mr-1" /> {importing ? "Importing…" : "Import CSV"}
        </button>
      </div>
    </div>
  );
}
